import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { Chart as ChartJS, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { SampleDataBadge } from '../../common';

// Register Chart.js components
ChartJS.register(...registerables, ChartDataLabels);

// Expand icon
const ExpandIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path
            d="M4 16V12M4 16H8M4 16L9 11M16 4V8M16 4H12M16 4L11 9"
            stroke="#666666"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

/**
 * Individual TL Footfall Bar Chart
 * Shows daily footfall with week pagination
 */
const MBFIndividualFootfallChart = ({ data = [], isSampleData = false, onMaximize, isFullscreen = false }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [currentWeek, setCurrentWeek] = useState(1);
    const totalWeeks = 2;

    // Default data
    const defaultData = [
        { day: 'Mon', footfall: 45 },
        { day: 'Tue', footfall: 38 },
        { day: 'Wed', footfall: 55 },
        { day: 'Thu', footfall: 60 },
        { day: 'Fri', footfall: 50 },
        { day: 'Sat', footfall: 75 },
        { day: 'Sun', footfall: 72 },
    ];

    // Transform API data to chart format
    // API returns: [{ day: 'Mon', footfall: 0 }, ...]
    const chartData = data.length > 0 ? data : defaultData;
    const days = chartData.map(item => item.day || '');
    const footfallValues = chartData.map(item => item.footfall || 0);

    // Calculate dynamic max for Y-axis
    const maxValue = Math.max(...footfallValues, 0);
    const suggestedMax = maxValue === 0 ? 10 : Math.ceil(maxValue * 1.15);

    const handlePrevWeek = () => {
        setCurrentWeek((prev) => Math.max(1, prev - 1));
    };

    const handleNextWeek = () => {
        setCurrentWeek((prev) => Math.min(totalWeeks, prev + 1));
    };

    useEffect(() => {
        if (chartRef.current) {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            const ctx = chartRef.current.getContext('2d');
            const fontSize = isMobile ? 10 : 12;
            const barThickness = isMobile ? 20 : 30;

            chartInstance.current = new ChartJS(ctx, {
                type: 'bar',
                data: {
                    labels: days,
                    datasets: [
                        {
                            label: 'Footfall',
                            data: footfallValues,
                            backgroundColor: '#FFA5CB',
                            borderColor: '#FFA5CB',
                            borderWidth: 0,
                            borderRadius: 4,
                            barThickness: barThickness,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            enabled: true,
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            titleColor: '#333333',
                            bodyColor: '#333333',
                            borderColor: '#e6e6e6',
                            borderWidth: 1,
                            padding: 10,
                            cornerRadius: 8,
                            callbacks: {
                                label: (context) => `Footfall: ${context.raw}`,
                            },
                        },
                        datalabels: {
                            display: false,
                        },
                    },
                    scales: {
                        x: {
                            grid: { display: false },
                            border: { display: false },
                            ticks: {
                                color: '#333333',
                                font: {
                                    family: '"Roboto", sans-serif',
                                    size: fontSize,
                                },
                            },
                        },
                        y: {
                            min: 0,
                            suggestedMax: suggestedMax,
                            ticks: {
                                color: '#4d4d4d',
                                font: {
                                    family: '"Roboto", sans-serif',
                                    size: fontSize,
                                },
                            },
                            border: { display: false },
                            grid: { color: '#f0f0f0' },
                        },
                    },
                },
            });
        }

        return () => {
            if (chartInstance.current) chartInstance.current.destroy();
        };
    }, [isMobile, days, footfallValues, suggestedMax]);

    // Fullscreen mode - render just the chart without wrapper
    if (isFullscreen) {
        return (
            <Box sx={{ width: '100%', height: '100%', p: 2 }}>
                <canvas ref={chartRef} />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                bgcolor: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #e6e6e6',
                display: 'flex',
                flexDirection: 'column',
                height: '380px',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    bgcolor: '#ffffff',
                    borderBottom: '1px solid #e6e6e6',
                    padding: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography
                        sx={{
                            color: '#333333',
                            fontFamily: '"Roboto", sans-serif',
                            fontSize: '16px',
                            lineHeight: '20px',
                            fontWeight: 500,
                        }}
                    >
                        Individual TL Footfall
                    </Typography>
                    {isSampleData && <SampleDataBadge absolute={false} />}
                </Box>

                <IconButton
                    size="small"
                    onClick={onMaximize}
                    sx={{
                        bgcolor: '#ffffff',
                        border: '1px solid #e6e6e6',
                        borderRadius: '8px',
                        padding: '8px',
                        boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.05)',
                        '&:hover': { bgcolor: '#f9f9f9' },
                    }}
                >
                    <ExpandIcon />
                </IconButton>
            </Box>

            {/* Content Area */}
            <Box
                sx={{
                    bgcolor: '#fafafa',
                    padding: '12px',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                }}
            >
                {/* Chart Container */}
                <Box
                    sx={{
                        bgcolor: '#ffffff',
                        borderRadius: '8px',
                        border: '1px solid #e6e6e6',
                        padding: '12px',
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                    }}
                >
                    {/* Chart */}
                    <Box sx={{ flex: 1, minHeight: 0 }}>
                        <canvas ref={chartRef} />
                    </Box>

                    {/* Week Pagination */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            gap: '8px',
                            pt: 1,
                            flexShrink: 0,
                        }}
                    >
                        <IconButton
                            size="small"
                            onClick={handlePrevWeek}
                            disabled={currentWeek === 1}
                            sx={{
                                bgcolor: '#fafafa',
                                border: '1px solid #e6e6e6',
                                borderRadius: '8px',
                                padding: '4px',
                                '&:hover': { bgcolor: '#f0f0f0' },
                                '&:disabled': { opacity: 0.5 },
                            }}
                        >
                            <ChevronLeftIcon sx={{ fontSize: '16px', color: '#4d4d4d' }} />
                        </IconButton>

                        <Box
                            sx={{
                                bgcolor: '#fafafa',
                                border: '1px solid #e6e6e6',
                                borderRadius: '8px',
                                padding: '4px 8px',
                            }}
                        >
                            <Typography
                                sx={{
                                    color: '#4d4d4d',
                                    fontSize: '10px',
                                    fontFamily: '"Roboto", sans-serif',
                                }}
                            >
                                {String(currentWeek).padStart(2, '0')}/{String(totalWeeks).padStart(2, '0')}
                            </Typography>
                        </Box>

                        <IconButton
                            size="small"
                            onClick={handleNextWeek}
                            disabled={currentWeek === totalWeeks}
                            sx={{
                                bgcolor: '#fafafa',
                                border: '1px solid #e6e6e6',
                                borderRadius: '8px',
                                padding: '4px',
                                '&:hover': { bgcolor: '#f0f0f0' },
                                '&:disabled': { opacity: 0.5 },
                            }}
                        >
                            <ChevronRightIcon sx={{ fontSize: '16px', color: '#4d4d4d' }} />
                        </IconButton>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default MBFIndividualFootfallChart;
